import React, { useState } from 'react';
import {
    Typography,
    Button,
    Stack,
    Alert,
    Chip,
    Menu,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Warning
} from '@mui/icons-material';
import { useShipments } from '../hooks/useShipments';
import { useShipmentMethods } from '../hooks/useShipmentMethods';
import { Shipment } from '@domain/shipment/models/Shipment';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import ShipmentDetailDrawer from './details/ShipmentDetailDrawer';
import { EmptyState, Paper } from '@shared/ui/common';

import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { BottomSheetSize, useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { CreateShipmentFlow } from './create/CreateShipmentFlow';
import ShipmentActions from './details/ShipmentActions';
import CreateShipmentMethodDialog from './methods/CreateShipmentMethodDialog';
import EditShipmentMethodDialog from './methods/EditShipmentMethodDialog';
import { TransportMethodIcon } from '@features/shipments/components/shared/TransportMethodIcon';
import { format } from 'date-fns';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import { Icon } from '@shared/icons';


const Shipments: React.FC = () => {
    const { shipments, isLoading, error } = useShipments();
    const { shipmentMethods, createShipmentMethod } = useShipmentMethods();
    const { openDrawer, closeDrawer } = useGenericDrawer();

    const { openBottomSheet, closeBottomSheet } = useBottomSheet();
    const [createMethodDialogOpen, setCreateMethodDialogOpen] = useState(false);
    const [selectedShipmentMethod, setSelectedShipmentMethod] = useState<ShipmentMethod | undefined>(undefined);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleViewShipment = (shipment: Shipment) => {
        openDrawer(
            `Shipment ${shipment.name || ''}`,
            <ShipmentDetailDrawer
                shipmentId={shipment.id!}
                onClose={() => {
                    closeDrawer();
                }}
            />,
            undefined,
            <ShipmentActions shipment={shipment} />
        );
    };

    const handleCreateShipment = () => {
        openBottomSheet({
            title: 'Create Shipment',
            component: <CreateShipmentFlow onClose={closeBottomSheet} />,
            size: BottomSheetSize.MD
        });
    };

    const handleCreateShipmentMethod = async (newMethod: any) => {
        const result = await createShipmentMethod({
            transportMethod: newMethod.transportMethod,
            duration: newMethod.duration,
            name: newMethod.name,
        });

        if (result) {
            setCreateMethodDialogOpen(false);
        }
    };

    const handleSelectShipmentMethod = (methodId: string) => {
        setSelectedShipmentMethod(shipmentMethods?.find((method) => method.id === methodId));
    };

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load shipments: {(error as Error).message}
            </Alert>
        );
    }

    return (
        <>
            <Stack spacing={0}>

                <Stack py={2} direction="row" justifyContent="flex-end" alignItems="center" gap={2}>


                    <Button
                        variant="text"
                        color="secondary"
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                    >
                        Shipment Methods
                    </Button>

                    <Button variant="text" color="secondary" startIcon={<AddIcon />} onClick={handleCreateShipment}>
                        Create Shipment
                    </Button>
                </Stack>


                {/* Shipments List */}
                <Paper>
                    {isLoading ? (
                        <Typography>Loading shipments...</Typography>
                    ) : shipments?.length === 0 ? (
                        <EmptyState title="No shipments found" description="Create your first shipment to get started" action={{ label: 'Create Shipment', onClick: handleCreateShipment }} />
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Shipment</TableCell>
                                    <TableCell>Items</TableCell>
                                    <TableCell>Departure Date</TableCell>
                                    <TableCell>Arrival Date</TableCell>
                                    <TableCell>Processed Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {shipments?.map((shipment) => (
                                    <TableRow
                                        key={shipment.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleViewShipment(shipment)}
                                    >
                                        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <TransportMethodIcon size={40} transportMethod={shipment.transportMethod} />
                                            <Typography variant="body2" fontWeight="medium">
                                                {shipment.name || 'Untitled Shipment'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                {shipment.purchaseOrderItems.slice(0, 2).map((item) => (
                                                    <Chip
                                                        color="info"
                                                        label={item.product.title} size="small"
                                                        sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    />
                                                ))}
                                                {shipment.purchaseOrderItems.length > 2 && (
                                                    <Chip
                                                        color="info"
                                                        label={`+${shipment.purchaseOrderItems.length - 2}`} size="small" />
                                                )}

                                                {shipment.purchaseOrderItems.length === 0 && (
                                                    <Stack direction="row" alignItems="center" gap={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            Shipment is empty
                                                        </Typography>
                                                        <Warning color="secondary" fontSize="small" />
                                                    </Stack>
                                                )}
                                            </Stack>

                                        </TableCell>
                                        <TableCell>
                                            {format(shipment.departureDate, 'EEE, MMM d, yyyy')}
                                        </TableCell>
                                        <TableCell>
                                            {format(shipment.arrivalDate, 'EEE, MMM d, yyyy')}
                                        </TableCell>
                                        <TableCell>
                                            {format(shipment.processedDate, 'EEE, MMM d, yyyy')}
                                        </TableCell>



                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Paper>


            </Stack>

            <CreateShipmentMethodDialog
                open={createMethodDialogOpen}
                onClose={() => setCreateMethodDialogOpen(false)}
                onSuccess={handleCreateShipmentMethod}
            />

            <EditShipmentMethodDialog
                shipmentMethod={selectedShipmentMethod}
                open={!!selectedShipmentMethod}
                onClose={() => setSelectedShipmentMethod(undefined)}
                onFinish={() => setSelectedShipmentMethod(undefined)}
            />

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {shipmentMethods?.map((method) => (
                    <MenuItem
                        divider
                        key={method.id}
                        onClick={() => {
                            handleSelectShipmentMethod(method.id);
                            handleClose();
                        }}
                    >
                        <Stack
                            key={method.id}
                            direction="row"
                            gap={2}
                            justifyContent="flex-start"
                            alignItems="center"
                            flex={1}
                            py={1}
                        >
                            <TransportMethodIcon size={40} transportMethod={method.transportMethod} />
                            <Stack direction="column" alignItems="flex-start" gap={0} flex={1}>
                                <Typography variant="subtitle2" color={selectedShipmentMethod?.id === method.id ? 'primary.main' : 'text.primary'}>
                                    {method.name || method.transportMethod.charAt(0).toUpperCase() + method.transportMethod.slice(1)}
                                </Typography>
                                <Typography variant="subtitle2" color={selectedShipmentMethod?.id === method.id ? 'primary.main' : 'text.primary'}>
                                    {method.duration} days
                                </Typography>
                            </Stack>
                            <Icon icon="IcEdit" />
                        </Stack>
                    </MenuItem>
                ))}

                <MenuItem onClick={() => setCreateMethodDialogOpen(true)}>
                    <Stack direction="row" justifyContent="center" alignItems="center" gap={2} p={2}>
                        <AddIcon />
                        <Typography>Add Method</Typography>
                    </Stack>
                </MenuItem>
            </Menu >
        </>
    );
};

export default Shipments; 